<script lang="ts">
	import { initializeApp } from "firebase/app";
	import { getDatabase, ref, get, set } from "firebase/database";
	import { useNavigate } from "svelte-navigator";
	import { getContext } from "svelte";
	import { Button } from "attractions";
	import { snackbarContextKey } from "attractions/snackbar";
	import Fa from "svelte-fa";
	import { faLink } from "@fortawesome/free-solid-svg-icons";
	import type {
		ConstraintDescriptor,
		LesserPuzzle,
		Puzzle,
		RandomParams,
		Status,
	} from "../types";
	import {
		EXAMPLE_PUZZLE,
		liftConstraints,
		clone,
		liftPuzzle,
	} from "../utils/puzzle";
	import { shuffle } from "../utils/array";
	import { solve, randomPuzzle } from "../utils/worker";

	import PuzzleViewer from "./PuzzleViewer.svelte";
	import SolverControls from "./SolverControls.svelte";
	import ConstraintViewer from "./ConstraintViewer.svelte";
	import AppBar from "./AppBar.svelte";
	import { randomBase62 } from "../utils/common";

	export let puzzleID: string;

	const BASE_URL = "https://krakersolver.net";

	const firebaseApp = initializeApp({
		databaseURL:
			"https://kraker-e8fc0-default-rtdb.europe-west1.firebasedatabase.app/",
	});
	const database = getDatabase(firebaseApp);
	const navigate = useNavigate();
	const showSnackbar: any = getContext(snackbarContextKey);

	let showLinkTooltip: boolean = false;
	let initialPuzzle: Puzzle;
	let puzzle: Puzzle;

	const initPuzzle = () => {
		if (initialPuzzle) {
			puzzle = clone(initialPuzzle);
			evaluate(puzzle);
		}
	};

	$: {
		if (puzzleID) {
			get(ref(database, "puzzles/" + puzzleID)).then((data) => {
				if (data.exists()) {
					initialPuzzle = data.val();
					return;
				}
				navigate("not-found");
			});
		} else {
			initialPuzzle = EXAMPLE_PUZZLE;
		}
	}

	$: {
		initialPuzzle;
		initPuzzle();
	}

	let solveInProgress = false;

	let status: Status;
	let constraints: ConstraintDescriptor[];

	const SOLVE_WORKERS = 20;
	const SOLVE_STEPS = 500;

	const onPuzzleChange = (nextPuzzle: Puzzle) => {
		initialPuzzle = nextPuzzle;
	};

	const onGenerateRandomPuzzle = (randomParams: RandomParams) => {
		randomPuzzle(randomParams.features, randomParams.people).then(
			(lesser: LesserPuzzle) => {
				const liftedPuzzle = liftPuzzle(lesser);
				shuffle(liftedPuzzle.consts);
				initialPuzzle = liftedPuzzle;
			}
		);
	};

	const onCopyPublicLink = () => {
		const puzzleID = randomBase62();
		set(ref(database, "puzzles/" + puzzleID), puzzle);
		const puzzleURL = `${BASE_URL}/${puzzleID}`;
		navigator.clipboard.writeText(puzzleURL);
		showSnackbar({
			props: { text: "Copied puzzle link to clipboard." },
		});
	};

	const update = (nextStatus: Status) => {
		constraints = liftConstraints(puzzle, nextStatus.soln.satisfied);
		puzzle.feats = nextStatus.soln.feats;
		status = nextStatus;
	};

	const onFeatsChange = (nextFeats: string[][]) => {
		const nextPuzzle: Puzzle = { ...puzzle, feats: nextFeats };
		solve(nextPuzzle, 0).then((s: Status) => update(s));
	};

	const evaluate = (p: Puzzle) => {
		solve(p, 0).then((s: Status) => update(s));
	};

	const onReset = () => {
		evaluate(initialPuzzle);
	};

	const onShuffle = () => {
		puzzle.feats.forEach((arr) => shuffle(arr));
		evaluate(puzzle);
	};

	const onRun = (steps: number) => {
		solve(puzzle, steps).then((s: Status) => update(s));
	};

	const onSolve = () => {
		solveInProgress = true;
		const results = new Array(SOLVE_WORKERS);
		for (let i = 0; i < SOLVE_WORKERS; i += 1) {
			results[i] = solve(puzzle, SOLVE_STEPS);
		}
		Promise.all(results).then((statuses: Status[]) => {
			statuses.forEach((curStatus: Status) => {
				if (
					curStatus.soln.violated.length < status.soln.violated.length
				) {
					update(curStatus);
				}
			});
			solveInProgress = false;
		});
	};
</script>

<AppBar puzzle={initialPuzzle} {onPuzzleChange} {onGenerateRandomPuzzle} />

<div class="root-container">
	{#if status}
		<div class="main-container">
			<PuzzleViewer {puzzle} {onFeatsChange} {constraints} />
			<SolverControls
				{solveInProgress}
				{onReset}
				{onShuffle}
				{onRun}
				{onSolve}
			/>
		</div>
		<div class="side-container">
			<ConstraintViewer
				bind:constraints
				feats={puzzle.feats}
				featCount={puzzle.featNames.length}
			/>
		</div>

		<div
			class="public-link-container"
			on:mouseenter={() => (showLinkTooltip = true)}
			on:mouseleave={() => (showLinkTooltip = false)}
		>
			<Button filled on:click={onCopyPublicLink}>
				<Fa icon={faLink} />
			</Button>
			{#if showLinkTooltip}
				<div class="tooltip-content">Copy puzzle link.</div>
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	@use "common.scss";
	@import "theme.scss";

	.tooltip-content {
		position: absolute;
		bottom: 0.5rem;
		left: 4rem;
		width: 8rem;
		z-index: 100;
		border-radius: 16px;
		margin-left: 0.5rem;
		margin-right: 0.5rem;
		padding: 0.75rem 1rem 0.75rem 1rem;
		box-shadow: 0px 0px 4px -1px black;
		background: $snackbar-bg;
	}
	.root-container {
		padding-top: 2rem;
		padding-bottom: 2rem;
		width: 100%;
		display: flex;
		flex-direction: row;
		justify-content: center;
		gap: 1rem;
	}

	.main-container,
	.side-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
	}

	.public-link-container {
		position: fixed;
		bottom: 1rem;
		left: 1.25rem;
		z-index: 200;
	}
</style>
